"use client";

import Image from "next/image";
import Link from "next/link";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { FC, ReactNode } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScaffoldBounds {
  top: number;
  bottom: number;
  left: number;
  right: number;
  containerWidth: number;
  containerHeight: number;
}

// ---------------------------------------------------------------------------
// Tunable constants
// ---------------------------------------------------------------------------

const DOT_COLOR = "rgb(196, 203, 194)";
const DOT_SPACING = 8;
const JUNCTION_SIZE = 4;
const JUNCTION_GAP = 10;
const CONTENT_PAD = 8;

// ---------------------------------------------------------------------------
// Context — lets children register the element the scaffold should frame
// ---------------------------------------------------------------------------

const ScaffoldContext = createContext<((node: HTMLDivElement | null) => void) | null>(null);

/**
 * Call inside a `DottedScaffoldShell` to get a ref-callback.
 * Attach it to the element the scaffold lines should frame.
 */
export function useScaffoldContentRef() {
  return useContext(ScaffoldContext);
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

function hLineStyle(x: number, y: number, width: number): React.CSSProperties {
  return {
    position: "absolute",
    left: x,
    top: y,
    width: Math.max(0, width),
    height: 1,
    backgroundImage: `radial-gradient(circle, ${DOT_COLOR} 1px, transparent 1px)`,
    backgroundSize: `${DOT_SPACING}px 1px`,
    backgroundRepeat: "repeat-x",
  };
}

function vLineStyle(x: number, y: number, height: number): React.CSSProperties {
  return {
    position: "absolute",
    left: x,
    top: y,
    width: 1,
    height: Math.max(0, height),
    backgroundImage: `radial-gradient(circle, ${DOT_COLOR} 1px, transparent 1px)`,
    backgroundSize: `1px ${DOT_SPACING}px`,
    backgroundRepeat: "repeat-y",
  };
}

function junctionStyle(cx: number, cy: number): React.CSSProperties {
  return {
    position: "absolute",
    left: cx - JUNCTION_SIZE / 2,
    top: cy - JUNCTION_SIZE / 2,
    width: JUNCTION_SIZE,
    height: JUNCTION_SIZE,
    borderRadius: "50%",
    backgroundColor: DOT_COLOR,
  };
}

// ---------------------------------------------------------------------------
// DottedScaffold — pure visual (4 lines × 3 segments + 4 junction dots)
// ---------------------------------------------------------------------------

function DottedScaffold({ bounds }: { bounds: ScaffoldBounds | null }) {
  if (!bounds) {
    return null;
  }

  const { top, bottom, left, right, containerWidth, containerHeight } = bounds;

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {/* Left vertical — 3 segments */}
      <div style={vLineStyle(left, 0, top - JUNCTION_GAP)} />
      <div style={vLineStyle(left, top + JUNCTION_GAP, bottom - top - 2 * JUNCTION_GAP)} />
      <div
        style={vLineStyle(left, bottom + JUNCTION_GAP, containerHeight - bottom - JUNCTION_GAP)}
      />

      {/* Right vertical — 3 segments */}
      <div style={vLineStyle(right, 0, top - JUNCTION_GAP)} />
      <div style={vLineStyle(right, top + JUNCTION_GAP, bottom - top - 2 * JUNCTION_GAP)} />
      <div
        style={vLineStyle(right, bottom + JUNCTION_GAP, containerHeight - bottom - JUNCTION_GAP)}
      />

      {/* Top horizontal — 3 segments */}
      <div style={hLineStyle(0, top, left - JUNCTION_GAP)} />
      <div style={hLineStyle(left + JUNCTION_GAP, top, right - left - 2 * JUNCTION_GAP)} />
      <div style={hLineStyle(right + JUNCTION_GAP, top, containerWidth - right - JUNCTION_GAP)} />

      {/* Bottom horizontal — 3 segments */}
      <div style={hLineStyle(0, bottom, left - JUNCTION_GAP)} />
      <div style={hLineStyle(left + JUNCTION_GAP, bottom, right - left - 2 * JUNCTION_GAP)} />
      <div
        style={hLineStyle(right + JUNCTION_GAP, bottom, containerWidth - right - JUNCTION_GAP)}
      />

      {/* Junction dots */}
      <div style={junctionStyle(left, top)} />
      <div style={junctionStyle(right, top)} />
      <div style={junctionStyle(left, bottom)} />
      <div style={junctionStyle(right, bottom)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DottedScaffoldShell — full-page container with measurement logic
// ---------------------------------------------------------------------------

export const DottedScaffoldShell: FC<{ children: ReactNode }> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);
  const [bounds, setBounds] = useState<ScaffoldBounds | null>(null);

  const contentRefCb = useCallback((node: HTMLDivElement | null) => {
    setContentNode(node);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !contentNode) {
      return;
    }

    function measure() {
      const containerRect = container!.getBoundingClientRect();
      const contentRect = contentNode!.getBoundingClientRect();
      setBounds({
        top: contentRect.top - containerRect.top - CONTENT_PAD,
        bottom: contentRect.bottom - containerRect.top + CONTENT_PAD,
        left: contentRect.left - containerRect.left - CONTENT_PAD,
        right: contentRect.right - containerRect.left + CONTENT_PAD,
        containerWidth: containerRect.width,
        containerHeight: containerRect.height,
      });
    }

    const ro = new ResizeObserver(measure);
    ro.observe(container);
    ro.observe(contentNode);
    measure();

    return () => ro.disconnect();
  }, [contentNode]);

  return (
    <ScaffoldContext.Provider value={contentRefCb}>
      <div
        ref={containerRef}
        className="relative flex h-full flex-col items-center overflow-hidden"
        style={{ backgroundColor: "#fffefb" }}
      >
        <DottedScaffold bounds={bounds} />

        {/* Centered name logo — hidden on mobile, absolute on desktop */}
        <div className="absolute left-1/2 top-6 z-20 hidden -translate-x-1/2 sm:block">
          <Image src="/netic_name_logo.svg" alt="Netic" width={105} height={28} unoptimized />
        </div>

        {/* Footer — hidden on mobile, absolute on desktop */}
        <div className="absolute bottom-6 left-1/2 z-20 hidden -translate-x-1/2 items-center gap-6 text-xs font-medium text-muted-foreground sm:flex">
          <span>Copyright &copy; {new Date().getFullYear()} Netic Inc.</span>
          <Link
            href="https://www.netic.ai/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground no-underline transition-colors hover:text-foreground"
          >
            Privacy Policy
          </Link>
        </div>

        {/* Page content — scrollable when content overflows */}
        <div className="relative z-10 h-full w-full overflow-y-auto">
          <div className="flex min-h-full w-full flex-col items-center justify-center py-6">
            {children}
          </div>
        </div>
      </div>
    </ScaffoldContext.Provider>
  );
};
